import * as THREE from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import { API_BASE, apiAuthHeaders } from './api-client.js';
import type { PreviewMeshData } from './preview-renderer.js';

const SVG_MODULES = new Set([
  'svg_four_petal_profile',
  'svg_flat_panel_second',
  'svg_flower_extrude',
  'svg_shrimp_extrude',
]);

export function isSvgPreviewModule(moduleId: string): boolean {
  return SVG_MODULES.has(moduleId);
}

export async function fetchSvgPreviewMesh(
  moduleId: string,
  targetHeight: number,
  extrusion: number,
): Promise<PreviewMeshData> {
  const response = await fetch(`${API_BASE}/svg-preview-assets/${encodeURIComponent(moduleId)}`, {
    headers: apiAuthHeaders(),
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`SVG preview asset failed (${response.status})`);
  const svg = await response.text();
  return buildSvgPreviewMeshFromText(svg, targetHeight, extrusion);
}

export function buildSvgPreviewMeshFromText(
  svg: string,
  targetHeight: number,
  extrusion: number,
): PreviewMeshData {
  const parsed = new SVGLoader().parse(svg);
  const triangles: PreviewMeshData['triangles'] = [];
  const depth = Math.max(0.8, Math.min(12, extrusion));

  for (const path of parsed.paths) {
    for (const shape of path.toShapes(true)) {
      const geometry = new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: true,
        bevelThickness: Math.min(0.4, depth * 0.18),
        bevelSize: Math.min(0.4, depth * 0.18),
        bevelSegments: 3,
        curveSegments: 12,
      });
      geometry.computeVertexNormals();
      const position = geometry.getAttribute('position');
      const normal = geometry.getAttribute('normal');
      for (let i = 0; i + 2 < position.count; i += 3) {
        const v = (index: number) => ({ x: position.getX(index), y: -position.getY(index), z: position.getZ(index) });
        const n = { x: normal.getX(i), y: -normal.getY(i), z: normal.getZ(i) };
        triangles.push({ v0: v(i), v1: v(i + 1), v2: v(i + 2), normal: n, groupId: 0 });
      }
      geometry.dispose();
    }
  }
  if (!triangles.length) throw new Error('SVG produced no extruded shapes');

  // Validate the actual client-side ExtrudeGeometry mesh. Do not reuse the
  // C++ worker's analysis: that worker may still describe the old SVG parser.
  const edgeCounts = new Map<string, { count: number; a: { x: number; y: number; z: number }; b: { x: number; y: number; z: number } }>();
  const pointKey = (p: { x: number; y: number; z: number }) => `${Math.round(p.x * 1e5)},${Math.round(p.y * 1e5)},${Math.round(p.z * 1e5)}`;
  const addEdge = (a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }) => {
    const ka = pointKey(a), kb = pointKey(b);
    const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
    const item = edgeCounts.get(key);
    if (item) item.count += 1;
    else edgeCounts.set(key, { count: 1, a, b });
  };
  for (const t of triangles) {
    addEdge(t.v0, t.v1); addEdge(t.v1, t.v2); addEdge(t.v2, t.v0);
  }
  const openEdges = [...edgeCounts.values()].filter((edge) => edge.count !== 2);

  let minY = Infinity, maxY = -Infinity;
  for (const t of triangles) for (const p of [t.v0, t.v1, t.v2]) { minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); }
  const scale = maxY > minY ? targetHeight / (maxY - minY) : 1;
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const t of triangles) for (const p of [t.v0, t.v1, t.v2]) { p.x *= scale; p.y *= scale; p.z *= scale; minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minZ = Math.min(minZ, p.z); maxZ = Math.max(maxZ, p.z); }
  const cx = (minX + maxX) * 0.5, cy = (minY + maxY) * 0.5 * scale;
  for (const t of triangles) for (const p of [t.v0, t.v1, t.v2]) { p.x -= cx; p.y -= cy; }
  return {
    triangles,
    triangleCount: triangles.length,
    vertexCount: triangles.length * 3,
    parts: [],
    analysis: {
      skipped: false,
      isWatertight: openEdges.length === 0,
      nonManifoldEdgeCount: openEdges.length,
      nonManifoldEdges: openEdges.map((edge) => ({ groupId: 0, v0: edge.a, v1: edge.b, triangleCount: edge.count })),
    },
  };
}
